import Loader from "@components/Loader";
import Modal from "@components/Modal";
import { useAuthContext } from "@contexts/auth";
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SupplyItemDetailModal from "./SupplyItemDetailModal";
import CycleSelect, { ALL_CYCLES } from "@components/dashboard/CycleSelect";
import { isPresentationProject } from "@utils/presentationMode";

const BRL_FMT = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const DATE_FMT = new Intl.DateTimeFormat("pt-BR", { timeZone: "UTC" });
const formatBRL = (v) => BRL_FMT.format(v || 0);
const fmtDate = (d) => (d ? DATE_FMT.format(new Date(d)) : "");

const CLASS_FILTERS = { A: ["A"], AB: ["A", "B"], ABC: ["A", "B", "C"] };
const classOf = (totalPercentage, curveA, curveB) =>
  totalPercentage < curveA ? "A" : (totalPercentage < curveB ? "B" : "C");

const validMirrorCount = (value) => Number.isSafeInteger(value) && value >= 0;
const canAssertSiengeAbsence = (mirror, resourceCount) => (
  mirror?.state === "complete"
  && validMirrorCount(mirror.candidate_resource_count)
  && validMirrorCount(mirror.linked_resource_count)
  && mirror.candidate_resource_count === resourceCount
  && mirror.linked_resource_count <= mirror.candidate_resource_count
);

const SupplyMirrorStatus = ({ mirror, resourceCount }) => {
  if (!mirror) return null;
  const candidates = validMirrorCount(mirror.candidate_resource_count)
    ? mirror.candidate_resource_count
    : null;
  const linked = validMirrorCount(mirror.linked_resource_count)
    ? mirror.linked_resource_count
    : null;
  const complete = canAssertSiengeAbsence(mirror, resourceCount);
  let message = "Espelho Sienge sem confirmação de completude.";
  if (mirror.state === "partial" && candidates !== null && linked !== null) {
    message = `Espelho Sienge parcial: ${linked} de ${candidates} insumos possuem vínculo comprovado.`;
  } else if (complete) {
    message = `Espelho Sienge verificado: ${linked} de ${candidates} insumos possuem vínculo.`;
  }
  return <aside className="supply-mirror-status" role="status" aria-live="polite">
    <strong>{message}</strong>
    {!complete ? (
      <span>Ausências de vínculo não são tratadas como risco comprovado.</span>
    ) : null}
  </aside>;
};

const CronogramaSuprimentos = () => {
  const { activeProject } = useProjectContext();
  const { getSupplySchedule, setSupplyStageDate, reprogramSupplyNeedDate, toggleSupplyStageLock, ignoreSupplySuggestion, getSupplyItemDetail, setSupplyItemTemplate, updateProcurementTemplate } = useProjectActions();
  const { user } = useAuthContext();
  const { configurations: { curve: { curveA, curveB } } } = user;
  const isCompanyAdmin = (user?.roles ?? []).includes("companyAdmin");
  const readOnly = isPresentationProject(activeProject);
  const navigate = useNavigate();
  const [data, setData] = useState({ stages: [], templates: [], items: [] });
  const [loading, setLoading] = useState(false);
  const [classFilter, setClassFilter] = useState("A");
  const [searchFilter, setSearchFilter] = useState("");
  const [cycleFilter, setCycleFilter] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const absenceAuthoritative = canAssertSiengeAbsence(
    data.sienge_mirror,
    data.items?.length ?? 0,
  );

  useEffect(() => {
    if (!activeProject) {
      setData({ stages: [], templates: [], items: [] });
      setLoadError(null);
      setLoaded(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      setLoaded(false);
      try {
        const res = await getSupplySchedule(activeProject.wbs_code_id, activeProject.building_id);
        if (cancelled) return;
        if (!res || !Array.isArray(res.items)) {
          throw new Error("invalid_supply_schedule_response");
        }
        const normalized = {
          ...res,
          stages: Array.isArray(res?.stages) ? res.stages : [],
          templates: Array.isArray(res?.templates) ? res.templates : [],
          items: Array.isArray(res?.items) ? res.items : [],
        };
        setData(normalized);
        // Com mais de um ciclo, a visão inicial é "Todos" — senão o usuário acha
        // que o ciclo padrão contém todos os insumos e perde os demais de vista.
        setCycleFilter(normalized.templates.length > 1
          ? ALL_CYCLES
          : (res?.default_template_id ?? null));
      } catch {
        if (cancelled) return;
        setData({ stages: [], templates: [], items: [] });
        setCycleFilter(null);
        setLoadError("Não foi possível carregar o Cronograma de Suprimentos.");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeProject]);

  const sugActive = (suggested, need_date, dismissed) => {
    if (!suggested) return false;
    const t = new Date(suggested).getTime();
    if (need_date && t === new Date(need_date).getTime()) return false;
    if (dismissed && t === new Date(dismissed).getTime()) return false;
    return true;
  };
  const patchItem = (resource_id, patch) =>
    setData((d) => ({ ...d, items: d.items.map((i) => (i.resource_id === resource_id ? { ...i, ...patch } : i)) }));

  const templates = data.templates ?? [];
  const multiCycle = templates.length > 1;
  const activeTemplate = templates.find((t) => t._id === cycleFilter);
  const boardStages = activeTemplate?.stages ?? data.stages ?? [];

  const handleDrop = async (targetStageKey, resourceId) => {
    const item = (data.items ?? []).find((i) => String(i.resource_id) === String(resourceId));
    if (!item) return;
    // Na visão "Todos" as colunas são do ciclo padrão; um item de ciclo custom
    // sem essa etapa não tem onde gravar o avanço — orientar em vez de no-op.
    if (!(item.stages ?? []).some((s) => s.key === targetStageKey)) {
      setActionError(`O ciclo "${item.template_name}" deste insumo não tem essa etapa. Selecione o ciclo dele no filtro para mover o card.`);
      return;
    }
    const sorted = [...boardStages].sort((a, b) => a.order - b.order);
    const lastStageKey = sorted[sorted.length - 1]?.key;
    if (item.current_stage_key === targetStageKey && targetStageKey !== lastStageKey) return;

    const prev = { stages: item.stages, current_stage_key: item.current_stage_key, status: item.status };
    patchItem(item.resource_id, { current_stage_key: targetStageKey }); // optimistic
    const res = await setSupplyStageDate(activeProject.building_id, item.resource_id, targetStageKey);
    if (res && res.stages) {
      setActionError(null);
      patchItem(item.resource_id, { stages: res.stages, current_stage_key: res.current_stage_key, status: res.status });
    } else {
      patchItem(item.resource_id, prev); // rollback
      setActionError("Não foi possível mover o card. Tente novamente.");
    }
  };

  const handleReprogram = async (resource_id, need_date, source) => {
    const it = data.items.find((i) => i.resource_id === resource_id);
    const res = await reprogramSupplyNeedDate(activeProject.building_id, resource_id, need_date, source, it?.need_date_suggested);
    if (!res || !res.stages) { setActionError("Não foi possível reprogramar a data. Tente novamente."); return; }
    setActionError(null);
    const dismissed = source === "suggestion" ? null : (it?.need_date_suggested ?? it?.suggestion_dismissed ?? null);
    patchItem(resource_id, {
      need_date: res.need_date, stages: res.stages, current_stage_key: res.current_stage_key, status: res.status,
      suggestion_dismissed: dismissed,
      suggestion_active: sugActive(it?.need_date_suggested, res.need_date, dismissed),
    });
  };

  const handleToggleLock = async (resource_id, stage_key) => {
    const res = await toggleSupplyStageLock(activeProject.building_id, resource_id, stage_key);
    if (res && res.stages) {
      setActionError(null);
      patchItem(resource_id, { stages: res.stages, current_stage_key: res.current_stage_key, status: res.status });
    } else {
      setActionError("Não foi possível travar/destravar a etapa. Tente novamente.");
    }
  };

  const handleSetCycle = async (resource_id, template_id) => {
    const res = await setSupplyItemTemplate(activeProject.building_id, resource_id, template_id);
    if (res && res.stages) {
      setActionError(null);
      patchItem(resource_id, {
        template_id: res.template_id,
        template_name: res.template?.name,
        stages: res.stages,
        current_stage_key: res.current_stage_key,
        status: res.status,
      });
    } else {
      setActionError("Não foi possível trocar o ciclo do insumo. Tente novamente.");
    }
  };

  const handleCreateCycle = async (name, baseTemplateId) => {
    const base = templates.find((t) => t._id === baseTemplateId) || templates.find((t) => t.is_default) || templates[0];
    const res = await updateProcurementTemplate(activeProject.company_id, { action: "create", name, stages: base?.stages || [] });
    if (res?.template) {
      setActionError(null);
      setData((d) => ({ ...d, templates: [...(d.templates ?? []), res.template] }));
      return res.template;
    }
    setActionError(res?.message || "Não foi possível criar o ciclo.");
    return null;
  };

  const handleIgnore = async (resource_id, need_date_suggested) => {
    const res = await ignoreSupplySuggestion(activeProject.building_id, resource_id, need_date_suggested);
    if (res && res.suggestion_dismissed) {
      setActionError(null);
      patchItem(resource_id, { suggestion_active: false, suggestion_dismissed: res.suggestion_dismissed });
    } else {
      setActionError("Não foi possível ignorar a sugestão. Tente novamente.");
    }
  };

  const handleConfirmStage = async (resourceId, targetStageKey, effectiveDate) => {
    const item = (data.items ?? []).find((i) => i.resource_id === resourceId);
    if (!item) return;
    const prev = { stages: item.stages, current_stage_key: item.current_stage_key, status: item.status };
    patchItem(resourceId, { current_stage_key: targetStageKey }); // optimistic
    const res = await setSupplyStageDate(activeProject.building_id, resourceId, targetStageKey, effectiveDate, "sienge");
    if (res && res.stages) {
      setActionError(null);
      patchItem(resourceId, { stages: res.stages, current_stage_key: res.current_stage_key, status: res.status });
    } else {
      patchItem(resourceId, prev); // rollback
      setActionError("Não foi possível confirmar a etapa. Tente novamente.");
    }
  };

  const stages = [...boardStages].sort((a, b) => a.order - b.order);
  const allowed = CLASS_FILTERS[classFilter];
  const searchTerm = searchFilter.trim().toLowerCase();
  const inCurve = (data.items ?? [])
    .map((i) => ({ ...i, curve_class: i.curve_class ?? classOf(i.totalPercentage, curveA, curveB) }))
    .filter((i) => allowed.includes(i.curve_class))
    .filter((i) => !searchTerm || (i.resource_description || "").toLowerCase().includes(searchTerm));
  const showAllCycles = !cycleFilter || cycleFilter === ALL_CYCLES;
  const visible = multiCycle && !showAllCycles ? inCurve.filter((i) => i.template_id === cycleFilter) : inCurve;
  const cycleCount = (templateId) => inCurve.filter((i) => i.template_id === templateId).length;
  // Visão "Todos": coluna pela key da etapa atual; se o ciclo do item tem keys
  // próprias, encaixa pela POSIÇÃO da etapa (ordem) nas colunas do ciclo padrão.
  const boardKeys = new Set(stages.map((s) => s.key));
  const columnKeyFor = (i) => {
    if (boardKeys.has(i.current_stage_key)) return i.current_stage_key;
    const own = [...(i.stages ?? [])].sort((a, b) => a.order - b.order);
    const idx = own.findIndex((s) => s.key === i.current_stage_key);
    return stages[Math.min(Math.max(idx, 0), stages.length - 1)]?.key;
  };
  const stagesOfCycle = (templateId) =>
    [...(templates.find((t) => t._id === templateId)?.stages ?? data.stages ?? [])].sort((a, b) => a.order - b.order);

  return (
    <>
      {loading && <div className="margin-top-24"><Loader /></div>}
      <div className="cronograma-suprimentos">
        {loadError && (
          <div className="cronograma-error-banner" role="alert"
            style={{ background: "#fdecea", color: "#b71c1c", padding: "8px 12px", borderRadius: 6, marginBottom: 12 }}>
            {loadError}
          </div>
        )}
        {actionError && (
          <div className="cronograma-error-banner" role="alert"
            style={{ background: "#fdecea", color: "#b71c1c", padding: "8px 12px", borderRadius: 6, marginBottom: 12 }}>
            {actionError}
          </div>
        )}
        <SupplyMirrorStatus
          mirror={data.sienge_mirror}
          resourceCount={data.items?.length ?? 0}
        />
        <div className="flex gap-16 graph-labels">
          {[["A", "Curva A"], ["AB", "Curva A+B"], ["ABC", "Curva A+B+C"]].map(([k, label]) => (
            <h1 key={k} className={classFilter === k ? "active-graph" : ""} onClick={() => setClassFilter(k)}>{label}</h1>
          ))}
          <input
            type="search"
            className="kanban-search"
            placeholder="Buscar insumo…"
            aria-label="Buscar insumo"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
        </div>
        {multiCycle && (
          <div className="cronograma-cycle-picker margin-top-8">
            <label>Ciclo de compras</label>
            <CycleSelect templates={templates} value={cycleFilter} onChange={setCycleFilter} count={cycleCount} allowAll />
          </div>
        )}
        {loaded && !loading && !loadError && visible.length === 0 && (
          <p>Nenhum insumo encontrado para os filtros selecionados.</p>
        )}
        <div className="kanban-board">
          {stages.map((stage) => {
            const cards = visible.filter((i) => columnKeyFor(i) === stage.key);
            return (
              <div
                key={stage.key}
                className="kanban-column"
                onDragOver={readOnly ? undefined : (e) => e.preventDefault()}
                onDrop={readOnly ? undefined : (e) => { e.preventDefault(); handleDrop(stage.key, e.dataTransfer.getData("text/plain")); }}
              >
                <header className="kanban-column-header">
                  <span className="kanban-stage-name">{stage.name}</span>
                  <span className="kanban-count">{cards.length}</span>
                </header>
                <div className="kanban-cards">
                  {cards.map((i) => {
                    const limite = i.stages?.find((s) => s.key === i.current_stage_key)?.data_limite;
                    const isManual = i.need_date_suggested && i.need_date &&
                      new Date(i.need_date).getTime() !== new Date(i.need_date_suggested).getTime();
                    return (
                      <div
                        key={i.resource_id}
                        className={`kanban-card curve-${i.curve_class} status-${i.status}`}
                        draggable={!readOnly}
                        onDragStart={readOnly ? undefined : (e) => e.dataTransfer.setData("text/plain", String(i.resource_id))}
                        onClick={() => setSelectedId(i.resource_id)}
                      >
                        {isManual && <span className="kanban-card-badge" title="Data definida manualmente">manual</span>}
                        {multiCycle && showAllCycles && i.template_id !== data.default_template_id && (
                          <span className="kanban-card-cycle" title={`Ciclo de compras: ${i.template_name}`}>{i.template_name}</span>
                        )}
                        <span className="kanban-card-title" title={i.resource_description}>{i.resource_description}</span>
                        <span className="kanban-card-sub">{i.measurement_unit} · {formatBRL(i.evaluated_total)}</span>
                        {limite && <span className="kanban-card-date">limite: {fmtDate(limite)}</span>}
                        {i.sienge?.order && (
                          <span
                            className="kanban-card-sienge"
                            title={`Pedido no Sienge${i.sienge.order.orders_count > 1 ? ` (+${i.sienge.order.orders_count - 1} pedidos)` : ""}`}
                          >
                            OC {i.sienge.order.formatted_id}
                            {i.sienge.order.supplier_name ? ` · ${i.sienge.order.supplier_name}` : ""}
                            {i.sienge.financial?.committed_pct != null ? ` · ${i.sienge.financial.committed_pct}% comprado` : ""}
                          </span>
                        )}
                        {(i.sienge?.alerts ?? []).includes("financeiro_vencido")
                          || (absenceAuthoritative
                            && (i.sienge?.alerts ?? []).includes("sem_fornecedor")) ? (
                          <span className="kanban-card-alerts">
                            {i.sienge.alerts.includes("financeiro_vencido") && (
                              <span className="alert-chip alert-chip--overdue" title="OC autorizada com parcela vencida em aberto — acionar financeiro">parcela vencida</span>
                            )}
                            {absenceAuthoritative && i.sienge.alerts.includes("sem_fornecedor") && (
                              <span className="alert-chip alert-chip--no-supplier" title="Sem OC autorizada perto do limite de contratação">sem fornecedor</span>
                            )}
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {(() => {
          const raw = (data.items ?? []).find((i) => i.resource_id === selectedId);
          if (!raw) return null;
          const sel = { ...raw, curve_class: raw.curve_class ?? classOf(raw.totalPercentage, curveA, curveB) };
          return (
            <Modal close={() => setSelectedId(null)}>
              <SupplyItemDetailModal
                item={sel}
                canAssertSiengeAbsence={absenceAuthoritative}
                stageMeta={sel.template_id ? stagesOfCycle(sel.template_id) : stages}
                templates={templates}
                buildingId={activeProject.building_id}
                getSupplyItemDetail={getSupplyItemDetail}
                onReprogram={readOnly ? null : (need_date, source) => handleReprogram(sel.resource_id, need_date, source)}
                onToggleLock={readOnly ? null : (stage_key) => handleToggleLock(sel.resource_id, stage_key)}
                onIgnoreSuggestion={readOnly ? null : () => handleIgnore(sel.resource_id, sel.need_date_suggested)}
                onSetCycle={readOnly ? null : (template_id) => handleSetCycle(sel.resource_id, template_id)}
                onCreateCycle={readOnly ? null : handleCreateCycle}
                onConfirmStage={readOnly ? null : (stage_key, effective_date) => handleConfirmStage(sel.resource_id, stage_key, effective_date)}
                onEditCycles={!readOnly && isCompanyAdmin ? () => { setSelectedId(null); navigate("/dashboard/settings?section=procurement"); } : null}
              />
            </Modal>
          );
        })()}
      </div>
    </>
  );
};

export default CronogramaSuprimentos;
